import React, { useState } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, TextField, FormControl,
  InputLabel, Select, MenuItem, Button, Divider, InputAdornment,
  FormControlLabel, Checkbox, Alert
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  AccountBalance as BankIcon,
  Description as DocumentIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';

const AICPolicyForm = ({ onSubmit, loading = false, initialData = {} }) => {
  const [formData, setFormData] = useState({
    // Policy Information
    issuing_office: initialData.issuing_office || '',
    proposal_date: initialData.proposal_date || new Date().toISOString().split('T')[0],
    
    // Insured Information
    insured_name: initialData.insured_name || '',
    insured_address: initialData.insured_address || '',
    property_address: initialData.property_address || '',
    
    // Intermediary Information
    intermediary_name: initialData.intermediary_name || '',
    intermediary_contact: initialData.intermediary_contact || '',
    intermediary_channel_type: initialData.intermediary_channel_type || '',
    sales_channel_code: initialData.sales_channel_code || '',
    
    // Aquaculture Details
    licensing_authority_reg_no: initialData.licensing_authority_reg_no || '',
    
    // Financial Information
    hypothecation_bank_name: initialData.hypothecation_bank_name || '',
    hypothecation_bank_branch: initialData.hypothecation_bank_branch || '',
    authorized_data_agency: initialData.authorized_data_agency || '',
    loan_account_no: initialData.loan_account_no || '',
    
    // Coverage Options
    covers_opted: initialData.covers_opted || 'Plan A',
    
    // Premium Calculation
    total_sum_insured: initialData.total_sum_insured || 0,
    deductible_percentage: initialData.deductible_percentage || 20,
    premium_amount: initialData.premium_amount || 0,
    total_discount: initialData.total_discount || 0,
    total_loadings: initialData.total_loadings || 0,
    gst_amount: initialData.gst_amount || 0,
    sgst_amount: initialData.sgst_amount || 0,
    cgst_amount: initialData.cgst_amount || 0,
    igst_amount: initialData.igst_amount || 0,
    final_premium: initialData.final_premium || 0
  });

  const [errors, setErrors] = useState({});

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required fields validation
    if (!formData.insured_name.trim()) newErrors.insured_name = 'Insured name is required';
    if (!formData.insured_address.trim()) newErrors.insured_address = 'Insured address is required';
    if (!formData.property_address.trim()) newErrors.property_address = 'Property address is required';
    if (!formData.licensing_authority_reg_no.trim()) newErrors.licensing_authority_reg_no = 'Licensing authority registration number is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    }
  };

  const calculatePremium = () => {
    const sumInsured = parseFloat(formData.total_sum_insured) || 0;
    const deductible = (sumInsured * parseFloat(formData.deductible_percentage)) / 100;
    const basePremium = sumInsured * 0.05; // 5% base rate
    const discount = parseFloat(formData.total_discount) || 0;
    const loading = parseFloat(formData.total_loadings) || 0;
    
    const premiumAfterDiscount = basePremium - discount + loading;
    const gst = premiumAfterDiscount * 0.18;
    const sgst = premiumAfterDiscount * 0.09;
    const cgst = premiumAfterDiscount * 0.09;
    const finalPremium = premiumAfterDiscount + gst;
    
    setFormData(prev => ({
      ...prev,
      deductible_amount: deductible,
      premium_amount: basePremium,
      gst_amount: gst,
      sgst_amount: sgst,
      cgst_amount: cgst,
      final_premium: finalPremium
    }));
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Card sx={{ mb: 3, bgcolor: 'primary.main', color: 'white' }}>
        <CardContent>
          <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <BusinessIcon sx={{ mr: 2 }} />
            Agriculture Insurance Company of India Ltd.
          </Typography>
          <Typography variant="h6">
            Aquaculture Insurance Policy Form
          </Typography>
        </CardContent>
      </Card>

      {/* Policy Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <DocumentIcon sx={{ mr: 1 }} />
            Policy Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Policy No."
                value={formData.policy_number || 'Auto-generated'}
                disabled
                helperText="Will be generated automatically"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Issuing Office"
                value={formData.issuing_office}
                onChange={handleChange('issuing_office')}
                placeholder="e.g., Mumbai Regional Office"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Date of Proposal & Declaration"
                type="date"
                value={formData.proposal_date}
                onChange={handleChange('proposal_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Period of Insurance - From"
                type="date"
                value={formData.policy_start_date || ''}
                onChange={handleChange('policy_start_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Period of Insurance - To"
                type="date"
                value={formData.policy_end_date || ''}
                onChange={handleChange('policy_end_date')}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Insured Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ mr: 1 }} />
            Insured Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name & Address of the Insured"
                value={formData.insured_name}
                onChange={handleChange('insured_name')}
                error={!!errors.insured_name}
                helperText={errors.insured_name}
                multiline
                rows={2}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Address of the site of the property to be insured"
                value={formData.property_address}
                onChange={handleChange('property_address')}
                error={!!errors.property_address}
                helperText={errors.property_address}
                multiline
                rows={2}
                required
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Intermediary Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Intermediary Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name of Intermediary (If any)"
                value={formData.intermediary_name}
                onChange={handleChange('intermediary_name')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Contact No."
                value={formData.intermediary_contact}
                onChange={handleChange('intermediary_contact')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Intermediary Channel Type"
                value={formData.intermediary_channel_type}
                onChange={handleChange('intermediary_channel_type')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Sales Channel Code"
                value={formData.sales_channel_code}
                onChange={handleChange('sales_channel_code')}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Aquaculture Details */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationIcon sx={{ mr: 1 }} />
            Aquaculture Details
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Water Spread Area of Pond (Ha)"
                type="number"
                value={formData.pond_area || ''}
                onChange={handleChange('pond_area')}
                InputProps={{
                  endAdornment: <InputAdornment position="end">Ha</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Shrimp/Prawn Species Insured"
                value={formData.species || ''}
                onChange={handleChange('species')}
                select
              >
                <MenuItem value="Vannamei">Vannamei</MenuItem>
                <MenuItem value="Monodon">Monodon</MenuItem>
                <MenuItem value="Scampi">Scampi</MenuItem>
                <MenuItem value="Indicus">Indicus</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Licensing Authority Registration No"
                value={formData.licensing_authority_reg_no}
                onChange={handleChange('licensing_authority_reg_no')}
                error={!!errors.licensing_authority_reg_no}
                helperText={errors.licensing_authority_reg_no}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Covers Opted</InputLabel>
                <Select
                  value={formData.covers_opted}
                  onChange={handleChange('covers_opted')}
                  label="Covers Opted"
                >
                  <MenuItem value="Plan A">Plan A - Basic Cover</MenuItem>
                  <MenuItem value="Plan B">Plan B - Comprehensive Cover</MenuItem>
                  <MenuItem value="Plan C">Plan C - Comprehensive Cover with Additional Diseases</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Financial Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <BankIcon sx={{ mr: 1 }} />
            Financial Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hypothecation Bank Name & Branch"
                value={formData.hypothecation_bank_name}
                onChange={handleChange('hypothecation_bank_name')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Authorized Data Agency for settlement"
                value={formData.authorized_data_agency}
                onChange={handleChange('authorized_data_agency')}
                placeholder="e.g., Indian Meteorological Department (IMD)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Loan A/c No."
                value={formData.loan_account_no}
                onChange={handleChange('loan_account_no')}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Premium Calculation */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <CalculateIcon sx={{ mr: 1 }} />
            Premium Calculation
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Sum Insured (Rs.)"
                type="number"
                value={formData.total_sum_insured}
                onChange={handleChange('total_sum_insured')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Deductible @20% (Rs.)"
                type="number"
                value={formData.deductible_amount || ''}
                disabled
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Premium (Rs.)"
                type="number"
                value={formData.premium_amount || ''}
                disabled
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Discount (Rs.)"
                type="number"
                value={formData.total_discount}
                onChange={handleChange('total_discount')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Total Loadings (Rs.)"
                type="number"
                value={formData.total_loadings}
                onChange={handleChange('total_loadings')}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="GST (Rs.)"
                type="number"
                value={formData.gst_amount || ''}
                disabled
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="SGST (Rs.)"
                type="number"
                value={formData.sgst_amount || ''}
                disabled
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="CGST (Rs.)"
                type="number"
                value={formData.cgst_amount || ''}
                disabled
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="IGST (Rs.)"
                type="number"
                value={formData.igst_amount || ''}
                disabled
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Premium after discount, loading and taxes (Rs.)"
                type="number"
                value={formData.final_premium || ''}
                disabled
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>
                }}
                sx={{ 
                  '& .MuiInputBase-input': { 
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    color: 'primary.main'
                  }
                }}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              onClick={calculatePremium}
              sx={{ mr: 2 }}
            >
              Calculate Premium
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Submit Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          size="large"
          disabled={loading}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
        >
          {loading ? 'Creating Policy...' : 'Create Policy'}
        </Button>
      </Box>
    </Box>
  );
};

export default AICPolicyForm;
